import { defineMessages } from "@formatjs/intl";
import { Loader } from "@googlemaps/js-api-loader";
import Supercluster, { PointFeature } from "supercluster";
import debounce from "lodash.debounce";

import { IContext } from "@src/context";
import { IProperty } from "../FeaturedProperties/types";
import { resolveUrl } from "@src/utils/baseUrl";
import fetchPropertiesJson from "../SearchResult/propertiesFetcher/allProperties";

import "./index.css";

interface IMarkerData {
  type: string;
  name: string;
  distance: string;
  time: string;
  lat: number;
  lng: number;
}

type ClusterProperties = {
  cluster: true;
  cluster_id: number;
  point_count: number;
  point_count_abbreviated: string;
};

type PropertyProperties = {
  id: number;
  name: string;
  address: string;
  origImg: string;
  webpImg: string;
  link: string;
};

type CustomProperties = ClusterProperties | PropertyProperties;

const messages = defineMessages({
  detailButton: {
    id: "MapFilter.DetailButton",
    defaultMessage: "Property Detail",
    description:
      "The label of the button to open property detail on the property",
  },
});

export function setupMapFilter(context: IContext) {
  const container = document.querySelector<HTMLElement>(
    ".container-map-filter"
  );

  if (!container) {
    return;
  }

  const { intl } = context;

  const mapFrame = document.getElementById("map-filter");
  const centerData = mapFrame?.getAttribute("data-center");
  const markersData = mapFrame?.getAttribute("data-markers");

  if (!mapFrame || !centerData || !markersData) {
    return;
  }

  const center: google.maps.LatLngLiteral = JSON.parse(centerData);
  const markers: IMarkerData[] = JSON.parse(markersData);

  const loader = new Loader({
    apiKey: import.meta.env.VITE_GOOGLE_MAP_KEY as string,
    version: "weekly",
  });

  const mapOptions: google.maps.MapOptions = {
    center,
    zoom: 15,
  };

  let filterCheckboxes: NodeListOf<HTMLInputElement>;
  let map: google.maps.Map | null = null;
  let infoWindow: google.maps.InfoWindow | null = null;
  let clusterer: Supercluster<CustomProperties>;

  const nearbyMarkers: google.maps.Marker[] = [];
  const propertyMarkers: Record<string, google.maps.Marker> = {};

  // Add center marker with enhanced InfoWindow
  const addCenterMarker = (mainProperty: IProperty) => {
    const marker = new google.maps.Marker({
      position: center,
      map,
      icon: {
        url: "/static/images/house-icon-pc.png",
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 40),
      },
      title: mainProperty.name,
    });

    marker.addListener("click", () => {
      if (infoWindow) {
        infoWindow.setContent(`
          <div class="container-map-filter-property">
            <picture>
              ${
                mainProperty.img?.webp
                  ? `<source type="image/webp" srcset="${resolveUrl(
                      mainProperty.img.webp
                    )}"></source>`
                  : ""
              }
              <img src="${
                mainProperty.img?.orig
                  ? resolveUrl(mainProperty.img.orig)
                  : resolveUrl("/static/images/default.jpg")
              }"></img>
            </picture>
            <h6>${mainProperty.name}</h6>
            <div class="element-address">
              <span>${mainProperty.addr}</span>
            </div>
          </div>
        `);
        infoWindow.open(map, marker);
      }
    });
  };

  // Remove unused checkboxes
  const filterUnusedCheckboxes = () => {
    const uniqueTypes = new Set(markers.map((marker) => marker.type));
    const filterList = container.querySelector<HTMLUListElement>(
      ".container-map-filter-list"
    );

    filterCheckboxes = container.querySelectorAll<HTMLInputElement>(
      ".container-map-filter-list input[type='checkbox']"
    );

    filterCheckboxes.forEach((checkbox) => {
      const checkboxType = checkbox.value;
      if (!uniqueTypes.has(checkboxType)) {
        checkbox.closest("li")?.remove();
      } else {
        checkbox.checked = true;
      }
    });

    filterList?.setAttribute("data-loaded", "");
  };

  // Update nearby markers based on selected types and zoom level
  const updateNearbyMarkers = () => {
    const selectedTypes = Array.from(filterCheckboxes)
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.value);

    const zoom = map?.getZoom() ?? 15;

    nearbyMarkers.forEach((marker, index) => {
      const markerType = markers[index]?.type; // Match marker with its type from the `markers` array

      if (zoom <= 10 || !selectedTypes.includes(markerType)) {
        marker.setMap(null); // Hide marker
      } else {
        marker.setMap(map); // Show marker
      }
    });
  };

  // Attach event listeners to checkboxes
  const bindCheckboxEvents = () => {
    filterCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", updateNearbyMarkers);
    });
  };

  // Add nearby markers to map
  const addNearbyMarkers = () => {
    markers.forEach((data) => {
      const marker = new google.maps.Marker({
        position: { lat: data.lat, lng: data.lng },
        map,
        icon: {
          url: `/static/images/markers/${data.type}.png?v=1`, // increment version to force cache busting
          scaledSize: new google.maps.Size(51, 60),
          anchor: new google.maps.Point(26, 60),
        },
        title: data.name,
      });

      // Add click event to show InfoWindow
      marker.addListener("click", () => {
        if (infoWindow) {
          infoWindow.setContent(`
            <div class="container-map-filter-nearby">
              <h6>${data.name}</h6>
              <p>${data.distance}</p>
              <p>${data.time}</p>
            </div>
          `);
          infoWindow.open(map, marker);
        }
      });

      nearbyMarkers.push(marker);
    });

    updateNearbyMarkers(); // Ensure markers are updated after initialization
  };

  // Create Supercluster instance for property markers
  const createClusterer = (properties: IProperty[]) => {
    clusterer = new Supercluster<CustomProperties>({
      radius: 60,
      maxZoom: 15,
    });

    const geoJSONPoints: PointFeature<CustomProperties>[] = properties
      .filter(
        (property) =>
          !(property.lat === center.lat && property.lng === center.lng) // Exclude main property
      )
      .map((property) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [property.lng ?? 0, property.lat ?? 0],
        },
        properties: {
          id: property.id ?? 0,
          name: property.name ?? "",
          address: property.addr ?? "",
          origImg: property.img?.orig ?? "",
          webpImg: property.img?.webp ?? "",
          link: property.detailLink ?? "",
        },
      }));

    clusterer.load(geoJSONPoints);
  };

  // Update property clusters
  const updatePropertyClusters = debounce(() => {
    if (!map || !clusterer) return;

    const bounds = map.getBounds();
    if (!bounds) return;

    const zoom = map.getZoom() ?? 15;
    const clusters = clusterer.getClusters(
      [
        bounds.getSouthWest().lng(),
        bounds.getSouthWest().lat(),
        bounds.getNorthEast().lng(),
        bounds.getNorthEast().lat(),
      ],
      zoom
    );

    const seenIds = new Set<string>();

    clusters.forEach((cluster) => {
      const { coordinates } = cluster.geometry;
      const [lng, lat] = coordinates;
      const property = cluster.properties;

      if (!property) return;

      if ("cluster" in property) {
        const clusterId = `cluster-${property.cluster_id}`;
        seenIds.add(clusterId);

        let marker = propertyMarkers[clusterId];
        if (!marker) {
          marker = new google.maps.Marker({
            position: { lat, lng },
            map,
            icon: {
              url: "/static/images/cluster.png",
              scaledSize: new google.maps.Size(56, 56),
            },
            label: {
              text: String(property.point_count),
              color: "white",
              fontSize: "11px",
              fontWeight: "bold",
            },
          });

          marker.addListener("click", () => {
            const expansionZoom = clusterer.getClusterExpansionZoom(
              property.cluster_id
            );
            map?.setZoom(expansionZoom);
            map?.panTo({ lat, lng });
          });

          propertyMarkers[clusterId] = marker;
        } else {
          marker.setPosition({ lat, lng });
          marker.setMap(map);
        }
      } else {
        const markerId = `property-${property.id}`;
        seenIds.add(markerId);

        let marker = propertyMarkers[markerId];
        if (!marker) {
          marker = new google.maps.Marker({
            position: { lat, lng },
            map,
            icon: {
              url: "/static/images/pin-off.png",
              scaledSize: new google.maps.Size(32, 32),
            },
            title: property.name,
          });

          marker.addListener("click", () => {
            if (infoWindow) {
              infoWindow.setContent(`
                <div class="container-map-filter-property">
                  <picture>
                    ${
                      property.webpImg
                        ? `<source type="image/webp" srcset="${resolveUrl(
                            property.webpImg
                          )}"></source>`
                        : ""
                    }
                    <img src="${
                      property.origImg
                        ? resolveUrl(property.origImg)
                        : resolveUrl("/static/images/default.jpg")
                    }"></img>
                  </picture>
                  <h6>${property.name}</h6>
                  <div class="element-address">
                    <span>${property.address}</span>
                  </div>
                  <a
                    class="button"
                    href="${property.link || ""}"
                    target="_blank"
                  >
                    <span>${intl.formatMessage(messages.detailButton)}</span>
                  </a>
                </div>
              `);
              infoWindow.open(map, marker);
            }
          });

          propertyMarkers[markerId] = marker;
        } else {
          marker.setPosition({ lat, lng });
          marker.setMap(map);
        }
      }
    });

    Object.keys(propertyMarkers).forEach((id) => {
      if (!seenIds.has(id)) {
        propertyMarkers[id].setMap(null);
      }
    });
  }, 300); // Debounce for 300ms

  // Initialize map and markers
  const loadMap = () => {
    if (map) return;

    loader.load().then(() => {
      map = new google.maps.Map(mapFrame, mapOptions);
      infoWindow = new google.maps.InfoWindow();

      fetchPropertiesJson(context).then((properties) => {
        // Find the main property and add its marker
        const mainProperty = properties.find(
          (property) =>
            property.lat === center.lat && property.lng === center.lng
        );

        if (mainProperty) {
          addCenterMarker(mainProperty);
        }

        // Exclude main property from the cluster
        const remainingProperties = properties.filter(
          (property) =>
            !(property.lat === center.lat && property.lng === center.lng)
        );

        createClusterer(remainingProperties);

        filterUnusedCheckboxes();
        addNearbyMarkers();
        bindCheckboxEvents();

        map?.addListener("zoom_changed", () => {
          updateNearbyMarkers();
          updatePropertyClusters();
        });

        map?.addListener("bounds_changed", updatePropertyClusters);
      });
    });
  };

  // Lazy load map
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        loadMap();
        observer.disconnect();
      }
    },
    { root: null, threshold: 0.1 }
  );

  observer.observe(mapFrame);
}
